import type { ReactNode } from 'react'
import { createContext, useEffect, useState } from 'react'

import type {
  ImageComponent as DSImageComponent,
  LinkComponent as DSLinkComponent,
} from '@twistopayments/design-system/base'
import { DesignSystemProvider } from '@twistopayments/design-system/base'
import { CssBaseline } from '@twistopayments/design-system/components/atoms/css-baseline'
import { Loader } from '@twistopayments/design-system/components/atoms/loader'
import { SvgGradient } from '@twistopayments/design-system/components/atoms/svg-gradient'
import { ToastProvider } from '@twistopayments/design-system/components/atoms/toast'
import { Version } from '@twistopayments/design-system/components/atoms/version'
import {
  ErrorBoundary,
  ErrorModalProvider,
} from '@twistopayments/design-system/components/molecules/error-boundary'
import NextImage from 'next/image'
import { isEmpty } from 'ramda'

import { AppConfigProvider, useSetupConfig } from '@twisto/base/app-config'
import { initDayjs } from '@twisto/base/dayjs'
import type { LoadMessages } from '@twisto/base/i18n-provider'
import { I18nProvider } from '@twisto/base/i18n-provider'
import { initShims } from '@twisto/base/shim'
import { Location } from '@twisto/components/atoms/location'
import { useConfigEditor } from '@twisto/components/organisms/config-editor'
import { analytics, logRegistrationErrorDialogView } from '@twisto/utils'

import { BrowserRouterWrapper } from './browser-router-wrapper'
import { NextLinkAdapter } from './next-link-adapter'
import { useThemeMode } from './theme-mode.hook'

type BaseAppContextType = { openConfigEditor: (() => void) | undefined }
export const BaseAppContext = createContext<BaseAppContextType>({
  openConfigEditor: undefined,
})

type BaseAppProps = {
  children: ReactNode
  onRouteChange?: () => void
  loadMessages: LoadMessages
  noBackground?: boolean
  hasBrowserRouter?: boolean
  configEditorContent?: ReactNode
}

export const BaseApp = ({
  children,
  onRouteChange,
  loadMessages,
  noBackground = false,
  hasBrowserRouter = true,
  configEditorContent,
}: BaseAppProps) => {
  const [shimLoaded, setShimLoaded] = useState(false)
  const themeMode = useThemeMode()

  const { openConfigEditor, ConfigEditor, configEditorOverride } =
    useConfigEditor(configEditorContent)
  const config = useSetupConfig({
    configEditorOverride,
  })
  const { language } = config
  useEffect(() => {
    // shim setup
    initShims({ language }).then(() => {
      initDayjs(language)
      setShimLoaded(true)
    })
  }, [language])

  // persist query string to gtm object on mount
  useEffect(() => {
    if (!isEmpty(window.location.search)) {
      analytics.setGlobalData({ query: window.location.search })
    }
  }, [])

  useEffect(() => {
    analytics.setUserSite(config.region)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleRouteChange = () => {
    // consumed by google tag manager
    const routeChangeEvent = new CustomEvent('routeChange')
    document.dispatchEvent(routeChangeEvent)

    onRouteChange && onRouteChange()

    return null
  }

  return (
    <DesignSystemProvider
      ImageComponent={NextImage as unknown as DSImageComponent}
      LinkComponent={NextLinkAdapter as unknown as DSLinkComponent}
      brand="b2c-legacy"
      callingCodes={config.callingCodes}
      currencyCode={config.currencyCode}
      currencyLocale={config.defaultRegionLanguage}
      formattedOptions={config.formattedOptions}
      support={config.support}
      supportEmail={config.support.email}
      themeMode={themeMode}
      onErrorModalOpen={logRegistrationErrorDialogView}
    >
      <CssBaseline
        fontScaling={config.featureFlags.fontScaling}
        noBackground={noBackground}
      />
      <SvgGradient />
      <AppConfigProvider config={config}>
        <BaseAppContext.Provider value={{ openConfigEditor }}>
          <BrowserRouterWrapper active={hasBrowserRouter}>
            <I18nProvider language={language} loadMessages={loadMessages}>
              <ErrorModalProvider>
                <Location>{handleRouteChange}</Location>
                {!shimLoaded ? (
                  <Loader />
                ) : (
                  <ErrorBoundary>
                    <ToastProvider>
                      {ConfigEditor && <ConfigEditor />}
                      {children}
                    </ToastProvider>
                  </ErrorBoundary>
                )}
              </ErrorModalProvider>
            </I18nProvider>
          </BrowserRouterWrapper>
        </BaseAppContext.Provider>
      </AppConfigProvider>
      <Version onClick={openConfigEditor} />
    </DesignSystemProvider>
  )
}
